import * as ko from "knockout";
import {IdName, IMessageData} from "../../../index";
import {KOSubscriptionTracker} from "../../../util/KOSubscriptionTracker";
import {IRequestObject} from "../../../req/index";
import PromiseRequest from "../../../req/PromiseRequest";
import {ErrorTracker} from "../../../error.tracker";
import {global} from "../../../appContext";
import {createErrorMessage, getObjectDefinition, IObjectDefinition} from "../../../util/misc";
import {memoryStore} from "../../../util/memorystore";
import {nsSyncFields} from "../../../req/nsSyncFields";
import {IDialogOptions} from "../../dialog/index";

export interface IAbstractCopyParams<TDataSrc = unknown> {
    dialog: IDialogOptions
    importformat: IdName
    language: IdName
    objecttype: IdName
    data: TDataSrc
}

export abstract class AbstractCopy<TDataSrc = unknown, TDataDst = unknown> {
    protected messageData: KnockoutObservable<IMessageData | null> = ko.observable(null);
    protected isReady: KnockoutObservable<boolean> = ko.observable(true);
    protected kosub: KOSubscriptionTracker = new KOSubscriptionTracker();
    protected reqGetSyncFields!: IRequestObject;
    protected reqCreate: PromiseRequest = new PromiseRequest('createsyncfielddisplayname');
    protected reqUpdate: PromiseRequest = new PromiseRequest('updatesyncfielddisplayname');

    protected readonly dataSrc: TDataSrc;
    protected dataDst: KnockoutObservable<TDataDst | null> = ko.observable(null);
    protected readonly oLanguage: KnockoutObservable<IdName>;
    protected readonly oImportFormat: KnockoutObservable<IdName | null> = ko.observable(null);

    protected errorTracker: ErrorTracker = global.errorAreas.get("config.syncfields.dialog.copy");
    protected objDef: IObjectDefinition;

    protected isFunction: boolean = false;

    constructor(protected params: IAbstractCopyParams<TDataSrc>) {
        this.objDef = getObjectDefinition(params.objecttype.id)!;
        this.oLanguage = memoryStore.get('config.syncfields.copy.language', params.language);
        this.oImportFormat = memoryStore.get('config.syncfields.copy.importformat', params.importformat);

        this.isFunction = params.objecttype.id == 4;

        this.kosub.subscribe(this.oLanguage, this.onFormChange.bind(this));
        this.kosub.subscribe(this.oImportFormat, this.onFormChange.bind(this));

        this.dataSrc = params.data;
    }

    private isValid = ko.pureComputed(() => this._isValid());

    protected _isValid(): boolean {
        const a = !!this.oLanguage();
        const b = !!this.oImportFormat();
        const c = this.params.language.id != this.oLanguage()?.id;
        const d = !this.isFunction && this.params.importformat.id != this.oImportFormat()?.id;
        const isDifferent = c || d;
        if (!isDifferent) {
            this.errorTracker.update(!isDifferent, this.oLanguage, 'same language for source and target');
            this.errorTracker.update(!isDifferent, this.oImportFormat, 'same import format for source and target');
        } else {
            this.errorTracker.update(!a, this.oLanguage, 'invalid value');
            this.errorTracker.update(!b, this.oImportFormat, 'invalid value');
        }
        return a && b && isDifferent;
    }

    private onFormChange() {
        if (!this._isValid()) {
            return;
        }
        this.dataDst(null);
        this.isReady(false);
        this.reqGetSyncFields = nsSyncFields.getSyncFields(this.params.objecttype.id, this.oLanguage()!.id, this.oImportFormat()!.id);
        this.reqGetSyncFields
            .request
            .then(this.onSyncfieldsLoaded.bind(this))
            .catch(reason => this.messageData(createErrorMessage(String(reason))));
    }

    protected abstract onSyncfieldsLoaded(data: any): void;

    protected abstract submit(): void;

    dispose() {
        this.reqCreate.abort();
        this.reqUpdate.abort();
        this.kosub.unsuscribeAll();
    }

}